import { recordFetchOutcome } from './network/fetchOutcomes';

/** Default Supabase HTTP timeout — fail fast on congested festival networks. */
export const SUPABASE_FETCH_TIMEOUT_MS = 9000;
/**
 * Signing up or in gets longer. The server does real work on /auth/v1/signup (bcrypt, the profile trigger) and a
 * festival cell with two bars can take more than the fail-fast budget to carry one request - at All Day I Dream
 * (2026-10-03) a sign-up with signal showing was told "No connection" at 9 s; the request never reached the edge.
 * The token refresh keeps the short budget: auth-js retries it, and the app stays signed in meanwhile.
 */
export const AUTH_FETCH_TIMEOUT_MS = 25000;

/** Sign-up, sign-in, recovery and code verification: the calls a person stands waiting on. Not logout or getUser. */
function isAuthRequest(input) {
  try {
    const url = typeof input === 'string' ? input : (input?.url ?? String(input));
    return (
      /\/auth\/v1\/(signup|recover|verify|otp|magiclink|resend)(\?|$)/.test(url) ||
      /\/auth\/v1\/token\?grant_type=(password|pkce)/.test(url)
    );
  } catch {
    return false;
  }
}

/** The budget for one request: the auth endpoints (not refresh) get AUTH_FETCH_TIMEOUT_MS when the default is in use. */
export function fetchTimeoutFor(input, timeoutMs = SUPABASE_FETCH_TIMEOUT_MS) {
  if (timeoutMs !== SUPABASE_FETCH_TIMEOUT_MS) return timeoutMs;
  return isAuthRequest(input) && !isRefreshTokenRequest(input) ? AUTH_FETCH_TIMEOUT_MS : timeoutMs;
}

function isAbortError(err) {
  return err?.name === 'AbortError' || err?.code === 'ABORT_ERR';
}

function mergeAbortSignals(signals) {
  const valid = signals.filter(Boolean);
  if (valid.length === 0) return undefined;
  if (valid.length === 1) return valid[0];

  const controller = new AbortController();
  const onAbort = () => controller.abort();

  valid.forEach((signal) => {
    if (signal.aborted) {
      controller.abort();
      return;
    }
    signal.addEventListener('abort', onAbort, { once: true });
  });

  return controller.signal;
}

/**
 * auth-js deletes the stored session (signs you out) when a refresh-token call fails with anything it does not
 * class as a network error - and it only counts a thrown fetch or 502/503/504 as network. A festival network also
 * answers with a 500/429 from an overloaded edge or an HTML page from a captive portal / proxy; none of those say
 * anything about the refresh token. Turn them into a thrown network error so auth-js keeps the session and retries.
 * A real verdict from the auth server (JSON 400/401/403: revoked or invalid refresh token) passes through untouched.
 */
function isRefreshTokenRequest(input) {
  try {
    const url = typeof input === 'string' ? input : (input?.url ?? String(input));
    return url.includes('/auth/v1/token') && url.includes('grant_type=refresh_token');
  } catch {
    return false;
  }
}

function isUntrustworthyAuthResponse(response) {
  if (!response) return true;
  if (response.status >= 500 || response.status === 429 || response.status === 408) return true;
  const type = response.headers?.get?.('content-type') || '';
  return !type.toLowerCase().includes('json');
}

/**
 * Fetch wrapper that aborts hung requests so UI can fall back to cached data.
 * Records outcomes for effective-connectivity (association != reachability).
 */
export function createTimeoutFetch(baseFetch = fetch, timeoutMs = SUPABASE_FETCH_TIMEOUT_MS) {
  return async (input, init = {}) => {
    const budgetMs = fetchTimeoutFor(input, timeoutMs);
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), budgetMs);
    const signal = mergeAbortSignals([init.signal, timeoutController.signal]);

    try {
      const response = await baseFetch(input, { ...init, signal });
      if (isRefreshTokenRequest(input) && isUntrustworthyAuthResponse(response)) {
        recordFetchOutcome({ ok: false, timeout: false });
        const netErr = new TypeError(
          `Token refresh got a non-auth response (${response?.status ?? 'none'})`,
        );
        netErr.outcomeRecorded = true;
        throw netErr;
      }
      recordFetchOutcome({ ok: true });
      return response;
    } catch (err) {
      if (err?.outcomeRecorded) throw err;
      const timedOut = isAbortError(err) && timeoutController.signal.aborted;
      recordFetchOutcome({ ok: false, timeout: timedOut });
      if (timedOut) {
        const timeoutErr = new Error(`Request timed out after ${budgetMs}ms`);
        timeoutErr.name = 'TimeoutError';
        timeoutErr.code = 'TIMEOUT';
        throw timeoutErr;
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  };
}
